//@flow

import React, {useState} from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Divider,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core';
import {
  Palette as PaletteIcon,
  Image as ImageIcon,
  TextFields as TextIcon,
  Warning,
} from '@material-ui/icons';
import {Alert, AlertTitle} from '@material-ui/lab';

import CustomModal from '../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import {getImageUrl} from '../../../utils/image_provider';
import {createFile} from '../../../utils/image';

import type {ProductVariantOption} from '../../../entities/product';
import type {CreateStoreProductOptionGroupRequest} from '../../../entities/product-option';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';

const useStyles = makeStyles(theme => ({
  modalContent: {
    maxWidth: 600,
    maxHeight: '80vh',
    overflow: 'auto',
  },
  header: {
    marginBottom: '.75rem',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.grey900,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: colors.grey600,
    fontSize: '0.875rem',
  },
  disclaimer: {
    marginBottom: '.75rem',
    paddingTop: '1rem',
  },
  disclaimerTitle: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
  },
  disclaimerList: {
    paddingLeft: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(0.5),
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  variantsList: {
    marginBottom: theme.spacing(3),
    maxHeight: 300,
    overflow: 'auto',
    border: `1px solid ${colors.grey300}`,
    borderRadius: 8,
  },
  variantItem: {
    borderBottom: `1px solid ${colors.grey200}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  variantContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1,
  },
  variantIcon: {
    fontSize: '1.25rem',
    color: colors.grey600,
  },
  variantDetails: {
    flex: 1,
  },
  variantName: {
    fontWeight: 500,
    color: colors.grey900,
  },
  variantLabel: {
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  valuesChips: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  valueChip: {
    height: 20,
    fontSize: '0.75rem',
    backgroundColor: colors.grey200,
    color: colors.grey700,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${colors.grey200}`,
  },
  importButton: {
    backgroundColor: colors.primary,
    color: 'white',
    '&:hover': {
      backgroundColor: colors.primaryDark,
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: colors.grey600,
  },
  stepper: {
    marginBottom: '.75rem',
    padding: '.5rem 0',
  },
  stepContent: {
    marginTop: '.5rem',
  },
  priceConfigSection: {
    marginBottom: '.75rem',
  },
  bulkPriceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: colors.grey100,
    borderRadius: 8,
  },
  bulkPriceField: {
    flex: 1,
  },
  priceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${colors.grey200}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  priceItemContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  priceField: {
    width: 120,
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${colors.grey200}`,
  },
  backButton: {
    color: colors.grey600,
  },
}));

type Props = {
  open: boolean,
  variants: ProductVariantOption[],
  onClose: () => void,
  onImport: (convertedGroups: CreateStoreProductOptionGroupRequest[]) => void,
};

export default function ImportVariantsModal({
  open,
  variants,
  onClose,
  onImport,
}: Props) {
  const classes = useStyles();
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(
    new Set()
  );
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [variantPrices, setVariantPrices] = useState<Map<string, number>>(
    new Map()
  );
  const [bulkPrice, setBulkPrice] = useState<string>('');
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(true);

  const steps = ['Seleccionar variantes', 'Configurar precios'];

  const handleVariantToggle = (variantId: string) => {
    const newSelected = new Set(selectedVariantIds);
    if (newSelected.has(variantId)) {
      newSelected.delete(variantId);
    } else {
      newSelected.add(variantId);
    }
    setSelectedVariantIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedVariantIds.size === variants.length) {
      setSelectedVariantIds(new Set());
    } else {
      setSelectedVariantIds(new Set(variants.map(v => v.id)));
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      // Initialize prices for selected variants
      const newPrices = new Map(variantPrices);
      const selectedVariants = variants.filter(v =>
        selectedVariantIds.has(v.id)
      );
      selectedVariants.forEach(variant => {
        if (!newPrices.has(variant.id)) {
          newPrices.set(variant.id, 0);
        }
      });
      setVariantPrices(newPrices);
      setCurrentStep(1);
    } else {
      handleImport();
    }
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  const handlePriceChange = (variantId: string, price: string) => {
    const numericPrice = parseFloat(price) || 0;
    setVariantPrices(prev => new Map(prev.set(variantId, numericPrice)));
  };

  const handleBulkPriceApply = () => {
    const price = parseFloat(bulkPrice) || 0;
    const newPrices = new Map(variantPrices);
    const selectedVariants = variants.filter(v => selectedVariantIds.has(v.id));
    selectedVariants.forEach(variant => {
      newPrices.set(variant.id, price);
    });
    setVariantPrices(newPrices);
    setBulkPrice('');
  };

  const convertVariantsToOptionGroups = async (
    selectedVariants: ProductVariantOption[]
  ): Promise<CreateStoreProductOptionGroupRequest[]> => {
    const convertedGroups = await Promise.all(
      selectedVariants.map(async variant => {
        const variantPrice = variantPrices.get(variant.id) || 0;
        const options = await Promise.all(
          variant.values.map(async (value, index) => {
            let imageFile = null;
            const id = crypto.randomUUID();

            if (value.imageId) {
              try {
                const imageUrl = getImageUrl(value.imageId);
                const {file} = await createFile(
                  imageUrl,
                  `variant-${value.name.replace(/\s+/g, '-').toLowerCase()}`
                );
                imageFile = file;
              } catch (error) {
                console.warn(
                  `Failed to create image file for variant value ${value.name}:`,
                  error
                );
              }
            }

            return {
              name: value.label || value.name,
              enabled: true,
              number: index + 1,
              color: value.color,
              price: variantPrice,
              imageFile,
              id,
            };
          })
        );

        return {
          name: variant.name,
          enabled: true,
          description:
            variant.label !== variant.name ? variant.label : undefined,
          options,
        };
      })
    );

    return convertedGroups;
  };

  const handleImport = async () => {
    try {
      setIsConverting(true);
      const selectedVariants = variants.filter(v =>
        selectedVariantIds.has(v.id)
      );
      const convertedGroups = await convertVariantsToOptionGroups(
        selectedVariants
      );
      onImport(convertedGroups);
      setSelectedVariantIds(new Set());
      setVariantPrices(new Map());
      setCurrentStep(0);
      setBulkPrice('');
    } catch (error) {
      console.error('Error converting variants:', error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleClose = () => {
    setSelectedVariantIds(new Set());
    setVariantPrices(new Map());
    setCurrentStep(0);
    setBulkPrice('');
    setShowAlert(true);
    onClose();
  };

  const getSubtitleText = () => {
    if (currentStep === 0) {
      return 'Selecciona las variantes que quieres convertir en opciones de producto';
    }
    return 'Configura el precio para cada variante seleccionada';
  };

  const getImportButtonText = () => {
    if (isConverting) {
      return 'Convirtiendo...';
    }
    return `Importar (${selectedVariants.length})`;
  };

  const getDisplayTypeIcon = (displayType: string) => {
    switch (displayType) {
      case 'COLOR':
        return <PaletteIcon className={classes.variantIcon} />;
      case 'IMAGE':
        return <ImageIcon className={classes.variantIcon} />;
      case 'TEXT':
      default:
        return <TextIcon className={classes.variantIcon} />;
    }
  };

  const selectedCount = selectedVariantIds.size;
  const isAllSelected = selectedCount === variants.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < variants.length;

  const selectedVariants = variants.filter(v => selectedVariantIds.has(v.id));

  const subtitleText = getSubtitleText();
  const importButtonText = getImportButtonText();

  return (
    <CustomModal open={open} onClose={handleClose}>
      <Box className={classes.modalContent}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            Importar desde Variantes de Producto
          </Typography>
          <Typography className={classes.subtitle}>{subtitleText}</Typography>
        </Box>

        <Stepper activeStep={currentStep} className={classes.stepper}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {currentStep === 0 && showAlert && (
          <Alert
            severity="warning"
            className={classes.disclaimer}
            onClose={() => setShowAlert(false)}
            icon={false}
          >
            <AlertTitle className={classes.disclaimerTitle}>
              <Warning />
              Importante: Limitaciones de la importación
            </AlertTitle>
            <ul className={classes.disclaimerList}>
              <li>
                Solo se importarán el nombre de la variante y de los valores: el
                nombre (label), color e imagen cuando estén disponibles.
              </li>
              <li>
                Las opciones importadas no se mantendrán sincronizadas con las
                variantes originales. Si modificas las variantes, deberás
                actualizar manualmente las opciones.
              </li>
              <li>
                Estas opciones son independientes a las variantes de producto y
                funcionan de forma diferente.
              </li>
            </ul>
          </Alert>
        )}

        <Box className={classes.stepContent}>
          {currentStep === 0 && (
            <>
              {variants.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Typography variant="h6" gutterBottom>
                    No hay variantes disponibles
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Primero debes crear variantes de producto para poder
                    importarlas
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={handleSelectAll}
                          color="primary"
                        />
                      }
                      label={`Seleccionar todas (${selectedCount}/${variants.length})`}
                    />
                  </Box>

                  <List className={classes.variantsList}>
                    {variants.map(variant => (
                      <ListItem
                        key={variant.id}
                        className={classes.variantItem}
                        button
                        onClick={() => handleVariantToggle(variant.id)}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={selectedVariantIds.has(variant.id)}
                            color="primary"
                            edge="start"
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box className={classes.variantContent}>
                              {getDisplayTypeIcon(variant.displayType)}
                              <Box className={classes.variantDetails}>
                                <Typography className={classes.variantName}>
                                  {variant.name}
                                </Typography>
                                <Typography className={classes.variantLabel}>
                                  {variant.label} • {variant.displayType}
                                </Typography>
                                <Box className={classes.valuesChips}>
                                  {variant.values.slice(0, 3).map(value => (
                                    <Chip
                                      key={value.id || value.name}
                                      label={value.label}
                                      size="small"
                                      className={classes.valueChip}
                                    />
                                  ))}
                                  {variant.values.length > 3 && (
                                    <Chip
                                      label={`+${variant.values.length -
                                        3} más`}
                                      size="small"
                                      className={classes.valueChip}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}

          {currentStep === 1 && (
            <Box className={classes.priceConfigSection}>
              {selectedVariants.length > 1 && (
                <Box className={classes.bulkPriceContainer}>
                  <CustomTextField
                    className={classes.bulkPriceField}
                    label="Precio"
                    type="number"
                    value={bulkPrice}
                    onChange={e => setBulkPrice(e.target.value)}
                    placeholder="0.00"
                    size="small"
                  />
                  <CustomButton
                    onClick={handleBulkPriceApply}
                    disabled={!bulkPrice || selectedVariants.length === 0}
                    variant="outlined"
                  >
                    Aplicar a todas
                  </CustomButton>
                </Box>
              )}
              <Divider style={{marginBottom: 16}} />

              <List className={classes.variantsList}>
                {selectedVariants.map(variant => (
                  <ListItem key={variant.id} className={classes.priceItem}>
                    <Box className={classes.priceItemContent}>
                      {getDisplayTypeIcon(variant.displayType)}
                      <Box className={classes.variantDetails}>
                        <Typography className={classes.variantName}>
                          {variant.name}
                        </Typography>
                        <Typography className={classes.variantLabel}>
                          {variant.values.length} valores
                        </Typography>
                      </Box>
                    </Box>
                    <CustomTextField
                      className={classes.priceField}
                      label="Precio"
                      type="number"
                      value={variantPrices.get(variant.id) || 0}
                      onChange={e =>
                        handlePriceChange(variant.id, e.target.value)
                      }
                      size="small"
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>

        <Box className={classes.navigationButtons}>
          {currentStep === 0 && (
            <>
              <CustomButton onClick={handleClose} variant="outlined">
                Cancelar
              </CustomButton>
              <CustomButton
                className={classes.importButton}
                onClick={handleNext}
                disabled={selectedCount === 0}
                variant="contained"
              >
                Siguiente {selectedCount > 0 ? `(${selectedCount})` : ''}
              </CustomButton>
            </>
          )}

          {currentStep === 1 && (
            <>
              <CustomButton
                className={classes.backButton}
                onClick={handleBack}
                variant="outlined"
              >
                Atrás
              </CustomButton>
              <CustomButton
                className={classes.importButton}
                onClick={handleNext}
                disabled={isConverting || selectedVariants.length === 0}
                variant="contained"
              >
                {importButtonText}
              </CustomButton>
            </>
          )}
        </Box>
      </Box>
    </CustomModal>
  );
}
